import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getAccountCapabilities, type Capability } from '@/lib/auth/capabilities';
import { Loader } from '@/components/ui/Loader';

interface AccountCapabilityGuardProps {
  requiredAny: readonly Capability[];
  children: React.ReactNode;
  redirectTo?: string;
}
/**
 * Strict account-plane guard. Unlike the general CapabilityGuard it never
 * falls back to a broader route prefix or scoped relationship authority.
 */
export function AccountCapabilityGuard({
  requiredAny,
  children,
  redirectTo = '/unauthorized',
}: AccountCapabilityGuardProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div aria-busy="true" className="min-h-[120px] flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  const capabilities = getAccountCapabilities(user);
  const allowed = requiredAny.some(capability => capabilities.includes(capability));
  if (!allowed) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
